import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from './Seo';
import SocialLinks from './SocialLinks';
import './LandingPage.css';

export default function LandingPage() {
  const heroBallsRef = useRef(null);
  const mockBarsRef = useRef(null);
  const [drawCount, setDrawCount] = useState(null);
  const [latestDraw, setLatestDraw] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const yearsOfData = new Date().getFullYear() - 2010;

  useEffect(() => {
    // Fetch live Powerball stats
    fetch('https://data.ny.gov/resource/d6yy-54nr.json?$select=count(*)')
      .then(r => r.json())
      .then(data => {
        const count = parseInt(data[0]?.count, 10);
        if (!isNaN(count)) setDrawCount(count.toLocaleString());
      })
      .catch(() => setDrawCount('2,800+'));

    fetch('https://data.ny.gov/resource/d6yy-54nr.json?$order=draw_date%20DESC&$limit=1')
      .then(r => r.json())
      .then(data => {
        if (!data[0]) return;
        const nums = data[0].winning_numbers.trim().split(' ').map(Number);
        const date = data[0].draw_date.substring(0, 10);
        setLatestDraw({
          main: nums.slice(0, 5),
          bonus: nums[5] || null,
          date,
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Generate floating hero balls
    const container = heroBallsRef.current;
    if (!container || container.children.length > 0) return;

    const colors = ['#D4A843','#2ECC85','#E74C3C','#378ADD','#F0C96A','#25A066'];
    for (let i = 0; i < 18; i++) {
      const b = document.createElement('div');
      const size = 40 + Math.random() * 60;
      const color = colors[Math.floor(Math.random() * colors.length)];
      b.className = 'lp-hero-ball';
      b.style.cssText = `
        width:${size}px;height:${size}px;
        left:${Math.random()*100}%;
        background:radial-gradient(circle at 35% 35%, ${color}cc, ${color}44);
        border:1px solid ${color}44;
        font-size:${size*0.35}px;
        color:${color};
        animation-duration:${12+Math.random()*20}s;
        animation-delay:${-Math.random()*20}s;
      `;
      b.textContent = Math.floor(1+Math.random()*59);
      container.appendChild(b);
    }

    // Generate mock bar chart
    const barsContainer = mockBarsRef.current;
    if (!barsContainer || barsContainer.children.length > 0) return;

    const barColors = ['#D85A30','#EF9F27','#888780','#378ADD'];
    for (let i = 0; i < 40; i++) {
      const h = 15 + Math.random() * 65;
      const c = barColors[Math.floor(Math.random()*barColors.length)];
      const bar = document.createElement('div');
      bar.className = 'lp-mock-bar';
      bar.style.cssText = `height:${h}%;background:${c};opacity:0.7;`;
      barsContainer.appendChild(bar);
    }
  }, []);

  return (
    <div className="landing-page">
      <Seo
        path="/"
        title="LottoPickle — Data-Driven Lottery Analysis for Powerball & Mega Millions"
        description="Frequency charts, heatmaps, trends, pair analysis, and a smart number picker for Powerball and Mega Millions. Play smarter with data-driven lottery stats."
      />
      {/* NAV */}
      <nav className="lp-nav">
        <Link to="/" className="lp-nav-logo">Lotto<span>Pickle</span></Link>
        <ul className="lp-nav-links">
          <li><a href="#features">Features</a></li>
          <li><a href="#how">How it works</a></li>
          <li><Link to="/about">About</Link></li>
          <li><Link to="/data-science">Data Science</Link></li>
          <li><Link to="/pricing">Pricing</Link></li>
        </ul>
        <button
          className={`lp-nav-toggle ${menuOpen ? 'open' : ''}`}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(o => !o)}
        >
          <span></span><span></span><span></span>
        </button>
        <Link to="/app" className="lp-nav-cta">Get Started</Link>
      </nav>

      {/* MOBILE MENU */}
      <div className={`lp-mobile-menu ${menuOpen ? 'open' : ''}`}>
        <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
        <a href="#how" onClick={() => setMenuOpen(false)}>How it works</a>
        <Link to="/about" onClick={() => setMenuOpen(false)}>About</Link>
        <Link to="/data-science" onClick={() => setMenuOpen(false)}>Data Science</Link>
        <Link to="/pricing" onClick={() => setMenuOpen(false)}>Pricing</Link>
      </div>

      {/* HERO */}
      <div className="lp-hero">
        <div className="lp-hero-balls" ref={heroBallsRef}></div>
        <div className="lp-hero-content">
          <p className="lp-hero-eyebrow">Lottery draw history, visualized</p>
          <h1 className="lp-hero-title">
            The numbers,<br />
            <span className="line2">explored.</span>
          </h1>
          <p className="lp-hero-sub">LottoPickle turns 15+ years of Powerball and Mega Millions draw history into clear, honest data visualizations. No predictions. No promises. Just the numbers.</p>
          <div className="lp-hero-actions">
            <Link to="/app" className="lp-btn-primary">Explore the data</Link>
            <a href="#features" className="lp-btn-secondary">See what's inside</a>
          </div>

          <div className="lp-hero-ticker">
            <p className="lp-ticker-label">
              Last Powerball draw{latestDraw ? ` — ${new Date(latestDraw.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
            </p>
            <div className="lp-ticker-balls">
              {latestDraw ? (
                <>
                  {latestDraw.main.map((n, i) => (
                    <div className="lp-t-ball white" key={i}>{n}</div>
                  ))}
                  {latestDraw.bonus && (
                    <div className="lp-t-ball red">{latestDraw.bonus}</div>
                  )}
                </>
              ) : (
                <>
                  {[1,2,3,4,5].map(i => <div className="lp-t-ball white" key={i}>·</div>)}
                  <div className="lp-t-ball red">·</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* STATS BAR */}
      <div className="lp-stats-bar">
        <div className="lp-stat-item">
          <span className="lp-stat-num">{drawCount ?? '—'}</span>
          <span className="lp-stat-label">Draws analyzed</span>
        </div>
        <div className="lp-stat-item">
          <span className="lp-stat-num">{yearsOfData}yrs</span>
          <span className="lp-stat-label">Historical data</span>
        </div>
        <div className="lp-stat-item">
          <span className="lp-stat-num">69</span>
          <span className="lp-stat-label">Numbers tracked</span>
        </div>
        <div className="lp-stat-item">
          <span className="lp-stat-num">9</span>
          <span className="lp-stat-label">Analysis modes</span>
        </div>
      </div>

      {/* FEATURES */}
      <section id="features" className="lp-section">
        <span className="lp-section-tag">What you get</span>
        <h2 className="lp-section-title">Every tool you need to<br /><em>explore the draw history.</em></h2>
        <p className="lp-section-sub">Built around one principle — show the historical data clearly and honestly, without interpretation. Live data from the NY State open data API, updated after every draw.</p>

        <div className="lp-features-grid">
          {[
            { num: '01', icon: '📊', cls: 'gold-icon', title: 'Frequency & Rankings', desc: "Bar chart of every number's full draw history, a hot/cold ranking view, and a color-coded heatmap — three ways to see the same data.", pro: false },
            { num: '02', icon: '📅', cls: 'green-icon', title: 'Draw History', desc: 'Paginated table of every recorded draw with date, main balls, and bonus ball. Color-coded by historical frequency at a glance.', pro: false },
            { num: '03', icon: '🎲', cls: 'gold-icon', title: 'Number Generator', desc: 'Generate picks using random, frequency-weighted, or most-overdue strategies. Every lottery draw is independent — this is for exploration.', pro: false },
            { num: '04', icon: '📈', cls: 'red-icon', title: 'Trends & Distribution', desc: 'Cumulative frequency curves across 2,800+ draws, plus histograms with mean, median, standard deviation, and skewness.', pro: true },
            { num: '05', icon: '🔗', cls: 'gold-icon', title: 'Gap & Pair Analysis', desc: 'Track how many draws have passed since each number last appeared, and see which pairs of numbers co-occur most often historically.', pro: true },
            { num: '06', icon: '🎯', cls: 'green-icon', title: 'Backtest My Numbers', desc: "Enter the numbers you play and see exactly how they would have performed across every historical draw — match counts, theoretical winnings, and a profile of each number.", pro: true },
          ].map((f) => (
            <div className="lp-feature-card" key={f.num}>
              <span className="lp-feature-num">{f.num}</span>
              {f.pro && <span className="lp-feature-pro-badge">Premium</span>}
              <div className={`lp-feature-icon ${f.cls}`}>{f.icon}</div>
              <h3 className="lp-feature-title">{f.title}</h3>
              <p className="lp-feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="lp-section-divider"></div>

      {/* HOW IT WORKS */}
      <div className="lp-how-section" id="how">
        <div className="lp-how-grid">
          <div>
            <span className="lp-section-tag">How it works</span>
            <h2 className="lp-section-title">Three steps to <em>explore the data</em></h2>
            <div className="lp-steps">
              {[
                { num: '01', title: 'Choose your game', desc: 'Select Powerball or Mega Millions. The full draw history loads instantly from the NY State open data API.' },
                { num: '02', title: 'Explore nine views', desc: 'Frequency charts, heatmaps, distribution histograms, gap tables, pair analysis, and more. Filter by date range to narrow the window.' },
                { num: '03', title: 'Generate numbers', desc: 'Use the number generator to produce a combination. All methods are statistically equivalent — lottery draws are independent random events.' },
              ].map((s) => (
                <div className="lp-step" key={s.num}>
                  <span className="lp-step-num">{s.num} —</span>
                  <div className="lp-step-content">
                    <h4>{s.title}</h4>
                    <p>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mock app */}
          <div className="lp-mock-app">
            <div className="lp-mock-header">
              <span className="lp-mock-title">LottoPickle</span>
              <span className="lp-mock-badge">Live</span>
            </div>
            <div className="lp-mock-bars" ref={mockBarsRef}></div>
            <div style={{display:'flex',justifyContent:'space-between',marginTop:'0.5rem'}}>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:'0.6rem',color:'rgba(200,185,138,0.4)'}}>1</span>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:'0.6rem',color:'rgba(200,185,138,0.4)'}}>59</span>
            </div>
            <div className="lp-mock-balls-row">
              <div className="lp-mock-ball lp-t-ball white">7</div>
              <div className="lp-mock-ball lp-t-ball white">23</div>
              <div className="lp-mock-ball lp-t-ball gold">31</div>
              <div className="lp-mock-ball lp-t-ball white">38</div>
              <div className="lp-mock-ball lp-t-ball white">45</div>
              <div className="lp-mock-ball lp-t-ball red">19</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA BANNER */}
      <div className="lp-cta-banner">
        <div className="lp-chip-deco" style={{width:300,height:300,background:'var(--gold)',top:-100,left:-80}}></div>
        <div className="lp-chip-deco" style={{width:200,height:200,background:'var(--emerald)',bottom:-60,right:-40}}></div>
        <>
          <h2>Ready to explore <em>the data?</em></h2>
          <p>Free to use. No account required. Just the numbers.</p>
          <Link to="/app" className="lp-btn-primary">Open the app</Link>
        </>
      </div>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-footer-top">
          <div className="lp-footer-logo">Lotto<span>Pickle</span></div>
          <SocialLinks variant="footer" />
        </div>
        <p className="lp-footer-disclaimer">For entertainment and data analysis purposes only. Historical lottery frequency data has no mathematical bearing on future draw outcomes. LottoPickle is not affiliated with any official lottery organization. Not gambling advice.</p>
        <p className="lp-footer-gambling">Must be 18+. If you or someone you know has a gambling problem, call the NCPG Helpline: <a href="tel:18005224700">1-800-522-4700</a></p>
        <ul className="lp-footer-links">
          <li><Link to="/about">About</Link></li>
          <li><Link to="/data-science">Data Science</Link></li>
          <li><Link to="/pricing">Pricing</Link></li>
          <li><Link to="/disclaimer">Disclaimer</Link></li>
          <li><Link to="/privacy">Privacy</Link></li>
          <li><Link to="/terms">Terms</Link></li>
          <li><Link to="/eula">EULA</Link></li>
          <li><Link to="/support">Support</Link></li>
          <li><a href="mailto:info@lottopickle.com">Contact</a></li>
        </ul>
      </footer>
    </div>
  );
}
